import React, { useState } from "react";
import { useTheme } from "../contexts/ThemeSelection";
import useAccentColor from "../hooks/useAccentColor";

type ButtonSize = "small" | "medium" | "large";
type ButtonVariant = "default" | "accent" | "secondary";

const sizeStyles: Record<ButtonSize, { padding: string; fontSize: string }> = {
  small: {
    padding: "6px 14px",
    fontSize: "0.8rem",
  },
  medium: {
    padding: "8px 18px",
    fontSize: "0.875rem",
  },
  large: {
    padding: "10px 24px",
    fontSize: "0.95rem",
  },
};

interface ThemedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  fullWidth?: boolean;
  variant?: ButtonVariant;
}

const ThemedButton: React.FC<ThemedButtonProps> = ({
  children,
  size = "medium",
  fullWidth = true,
  variant = "default",
  disabled,
  style: externalStyle,
  ...props
}) => {
  const { theme, activeTheme } = useTheme();
  const { accent, accentText } = useAccentColor();
  const [hovered, setHovered] = useState(false);

  const isHovered = hovered && !disabled;

  const variantMap: Record<ButtonVariant, React.CSSProperties> = {
    accent: {
      backgroundColor: accent,
      color: accentText,
      border: "none",
      borderRadius: "24px",
      boxShadow: isHovered ? "0 4px 14px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.15)",
      transform: isHovered ? "translateY(-1px)" : "none",
      fontWeight: 600,
    },
    secondary: {
      backgroundColor: isHovered ? "rgba(128,128,128,0.1)" : "transparent",
      color: theme[activeTheme].color,
      border: "1px solid rgba(128,128,128,0.25)",
      borderRadius: "10px",
      fontWeight: 500,
    },
    default: {
      backgroundColor: isHovered ? theme[activeTheme].selectedColor : theme[activeTheme].panelColor,
      color: theme[activeTheme].color,
      border: "none",
      borderRadius: "10px",
      fontWeight: 500,
    },
  };

  const variantStyles = variantMap[variant];

  const buttonStyle: React.CSSProperties = {
    ...variantStyles,
    padding: sizeStyles[size].padding,
    fontSize: sizeStyles[size].fontSize,
    cursor: disabled ? "not-allowed" : "pointer",
    outline: "none",
    transition: "all 0.2s ease",
    width: fullWidth ? "100%" : "auto",
    opacity: disabled ? 0.5 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    whiteSpace: "nowrap",
    ...externalStyle,
  };

  return (
    <button
      style={buttonStyle}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    >
      {children}
    </button>
  );
};

export default ThemedButton;
